<template>
    <div>
        <h1 class="dinlineb mb-4">{{ $l10n.__('Rapport - Rapprochement bancaire') }}</h1>

        <ul class="m-0 fright">
            <li>
                <CompassButton
                    :label="$t.__('report.Report.access_operation')"
                    @click="operationUrl"
                />
            </li>
        </ul>

        <div class="filters mb-6">
            <span class="mr-2 fs-3">{{ $l10n.__('Filtrer du') }}</span>
            <el-date-picker
                v-model="datePickerPeriod"
                type="daterange"
                align="left"
                unlink-panels
                range-separator="au"
                :start-placeholder="$t.__('common.labels.start_date')"
                :end-placeholder="$t.__('common.labels.end_date')"
                :format="datePickerFormat"
                value-format="yyyy-MM-dd"
                :picker-options="pickerOptions"
                prefix-icon=" "
                @change="refresh"
            />
        </div>

        <div v-if="load.reconcilied" class="content_grid">
            <div class="row">
                <!-- DOUGHNUT AND TABLE LEFT -->
                <div class="col span_6 pr-3">
                    <div class="doughnut mb-5">
                        <doughnut-chart :data="series.receiptGraph" :options="optionsDoughnit" />
                    </div>

                    <el-table
                        :data="series.receipt"
                        height="376"
                        style="width: 100%"
                        :header-cell-class-name="headerCellClassName"
                        :cell-class-name="tableCellClassName"
                    >
                        <el-table-column
                            prop="codeName"
                            width="376"
                            :label="$t.__('report.Report.recipes_detail')"
                        >
                        </el-table-column>
                        <el-table-column prop="total"> </el-table-column>
                        <el-table-column prop="percent"> </el-table-column>
                    </el-table>
                </div>

                <!-- DOUGHNUT AND TABLE RIGHT -->
                <div class="col span_6 pl-3">
                    <div class="doughnut mb-5">
                        <doughnut-chart :data="series.expenseGraph" :options="optionsDoughnit" />
                    </div>
                    <el-table
                        :data="series.expense"
                        height="376"
                        style="width: 100%"
                        :header-cell-class-name="headerCellClassName"
                        :cell-class-name="tableCellClassName"
                    >
                        <el-table-column
                            prop="codeName"
                            width="300"
                            :label="$t.__('report.Report.expenses_detail')"
                        >
                        </el-table-column>
                        <el-table-column prop="total"> </el-table-column>
                        <el-table-column prop="percent"> </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>

        <div v-else class="loader-container prelative mt-5">
            <preloader :display="!load.reconcilied" />
        </div>

        <h2 class="mt-6">{{ $l10n.__('Soldes des factures') }}</h2>

        <div class="row">
            <h3 class="mt-3">
                {{ $l10n.__('Toutes les factures non rapprochées') }}
                <span class="grey fs-3 regular ml-2">( {{ $l10n.__('Sauf brouillon') }} )</span>
            </h3>

            <div class="col span_6 pr-3">
                <highlight-number
                    v-if="load.docNoReconcilied"
                    :title="$t.__('report.Report.sales')"
                    :link="invoiceOtherLink"
                    :cells="noReconciliedCard(series.doc.other)"
                />

                <div v-else class="loader-container prelative mt-5 col span_6">
                    <preloader :display="!load.docNoReconcilied" />
                </div>
            </div>

            <div class="col span_6 pl-3">
                <highlight-number
                    v-if="load.purDocNoReconcilied"
                    :title="$t.__('report.Report.purchases')"
                    :link="purInvoiceOtherLink"
                    :cells="noReconciliedCard(series.purDoc.other)"
                />

                <div v-else class="loader-container prelative mt-5 col span_6">
                    <preloader :display="!load.purDocNoReconcilied" />
                </div>
            </div>
        </div>

        <div class="row">
            <h3>{{ $l10n.__('Factures au statut payées non rapprochées') }}</h3>

            <div class="col span_6 pr-3">
                <highlight-number
                    v-if="load.docNoReconcilied"
                    :title="$t.__('report.Report.sales')"
                    :link="invoicePaidLink"
                    :cells="noReconciliedCard(series.doc.paid)"
                />
            </div>

            <div class="col span_6 pl-3">
                <highlight-number
                    v-if="load.purDocNoReconcilied"
                    :title="$t.__('report.Report.purchases')"
                    :link="purInvoicePaidLink"
                    :cells="noReconciliedCard(series.purDoc.paid)"
                />
            </div>
        </div>

        <h2 class="mt-6 mb-1">{{ $l10n.__('Evolution des comptes bancaires') }}</h2>
        <h4 class="mt-0 mb-3 gray-60 regular">
            {{ $l10n.__('Courbes des soldes de vos comptes bancaires') }}
        </h4>

        <div class="row">
            <div v-if="load.balance" class="line-charts well well-no-shadow">
                <line-chart
                    :data="series.balanceGraph"
                    :options="lineChartOptions"
                    :total-line="true"
                    :height="800"
                ></line-chart>
            </div>
            <div v-else class="loader-container prelative mt-5 col span_6">
                <preloader :display="!load.balance" />
            </div>
        </div>
    </div>
</template>

<script>
import { goToUrl } from 'vueDir/helpers.js';
import datePickerShortCut from '../../mixins/datePickerOptionsRB.js';
import DoughnutChart from '../chart/Doughnut.js';
import LineChart from '../chart/LineChart.js';
import HighlightNumber from './HighlightNumber.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters } = createNamespacedHelpers('corp');

const colors = [
    '#FBD75B',
    '#FAD089',
    '#FFB878',
    '#FF9C5B',
    '#FF887C',
    '#F5634A',
    '#DBADFF',
    '#A280D6',
    '#A4BDFC',
    '#4BC9D8',
    '#7AE7BF',
    '#E1E1E1',
    '#516D7F',
    '#3399FF',
    '#1F6ED4',
];

const numFormat = {
    currency: App.numformat.currencysymbol,
    currencypos: App.numformat.currencypos,
    decimals: App.numformat.decimals,
    name: App.numformat.name,
    position: '',
    precision: App.numformat.precision,
    precisioncustom: App.numformat.precisioncustom,
    thousands: App.numformat.thousands,
};

export default {
    components: {
        Preloader,
        LineChart,
        DoughnutChart,
        'highlight-number': HighlightNumber,
    },
    mixins: [datePickerShortCut],
    props: {
        defaultFilter: Object,
    },
    data: function () {
        return {
            filters: {
                period: {
                    start: this.defaultFilter.start,
                    end: this.defaultFilter.end,
                },
            },
            optionsDoughnit: {
                legend: {
                    display: false,
                },
                tooltips: {
                    callbacks: {
                        label: function (tooltipItem, data) {
                            let index = tooltipItem.index;
                            let name = data.labels[index];
                            let value = data.datasets[0].data[index];
                            return (
                                name +
                                ' : ' +
                                NumberFormat.formatToDisplayWithCurrency(value, numFormat)
                            );
                        },
                    },
                },
            },
            defaultLegendClickHandler: Chart.defaults.global.legend.onClick,
            lineChartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                callback: function (value, index, values) {
                                    return NumberFormat.formatToDisplayWithCurrency(
                                        value,
                                        numFormat,
                                    );
                                },
                            },
                        },
                    ],
                },
            },
            series: {
                receipt: [],
                recseiptGraph: {},
                expense: [],
                expenseGraph: {},
                doc: {
                    paid: {
                        listingUrl: '',
                        taxesFree: 0,
                        taxes: 0,
                        amount: 0,
                    },
                    other: {
                        listingUrl: '',
                        taxesFree: 0,
                        taxes: 0,
                        amount: 0,
                    },
                },
                purDoc: {
                    paid: {
                        listingUrl: '',
                        taxesFree: 0,
                        taxes: 0,
                        amount: 0,
                    },
                    other: {
                        listingUrl: '',
                        taxesFree: 0,
                        taxes: 0,
                        amount: 0,
                    },
                },
                balanceGraph: {},
            },
            load: {
                reconcilied: false,
                docNoReconcilied: false,
                purDocNoReconcilied: false,
                balance: false,
            },
            datePickerPeriod: [this.defaultFilter.start, this.defaultFilter.end],
        };
    },
    computed: {
        invoicePaidLink: function () {
            return {
                label: this.$l10n.__('Consulter la liste des documents'),
                link: this.series.doc.paid.listingUrl,
            };
        },
        invoiceOtherLink: function () {
            return {
                label: this.$l10n.__('Consulter la liste des documents'),
                link: this.series.doc.other.listingUrl,
            };
        },
        purInvoicePaidLink: function () {
            return {
                label: this.$l10n.__('Consulter la liste des documents'),
                link: this.series.purDoc.paid.listingUrl,
            };
        },
        purInvoiceOtherLink: function () {
            return {
                label: this.$l10n.__('Consulter la liste des documents'),
                link: this.series.purDoc.other.listingUrl,
            };
        },

        ...mapGetters(['datePickerFormat']),
    },
    mounted: function () {
        $(document).bind('MyFacture.afterInit', () => {
            this.fetchReconcilied();
            this.fetchNoReconcilied('doc');
            this.fetchNoReconcilied('purDoc');
            this.fetchBalance();
        });
    },
    methods: {
        tableCellClassName: function ({ row, column, rowIndex, columnIndex }) {
            if (rowIndex < 15 && columnIndex == 0) {
                return 'bl-4-color' + (rowIndex + 1);
            } else if (rowIndex >= 15 && columnIndex == 0) {
                return 'bl-4-color15';
            }
        },
        headerCellClassName: function ({ row, rowIndex }) {
            return 'gray-100';
        },
        refresh: function (val) {
            this.filters = {
                period: {
                    start: val[0],
                    end: val[1],
                },
            };
            this.fetchReconcilied();
            this.fetchNoReconcilied('doc');
            this.fetchNoReconcilied('purDoc');
            this.fetchBalance();
        },
        fetchReconcilied: function () {
            this.load.reconcilied = false;

            const options = {
                report_reconciliation_period_start: this.filters.period.start,
                report_reconciliation_period_end: this.filters.period.end,
            };
            $.post(
                '/?_f=bankreconciliation_reconciliationStat&action=codeList',
                options,
                (data) => {
                    // Check errors
                    if (data.form.status == 'failed') {
                        if (data.form.msg != '') {
                            Push.error(data.form.msg);
                        } else if (data.form.fields.length > 0 && data.form.fields[0].msg) {
                            Push.error(data.form.fields[0].msg);
                        }
                    }

                    // Format and add currency
                    this.series.receipt = [];
                    data.receipt.forEach((line, index) => {
                        this.series.receipt.push({
                            codeName: line.codeName,
                            total: NumberFormat.formatToDisplayWithCurrency(line.total, numFormat),
                            percent: NumberFormat.formatToDisplay(line.percent, numFormat) + ' %',
                        });
                    });

                    this.series.expense = [];
                    data.expense.forEach((line, index) => {
                        this.series.expense.push({
                            codeName: line.codeName,
                            total: NumberFormat.formatToDisplayWithCurrency(line.total, numFormat),
                            percent: NumberFormat.formatToDisplay(line.percent, numFormat) + ' %',
                        });
                    });

                    this.series.receiptGraph = data.receiptGraph;
                    this.series.expenseGraph = data.expenseGraph;

                    //Add colors
                    this.series.receiptGraph.datasets[0].backgroundColor = [];
                    this.series.receiptGraph.datasets[0].data.forEach((element, index) => {
                        let indexColor = index < colors.length ? index : colors.length - 1;
                        this.series.receiptGraph.datasets[0].backgroundColor.push(
                            colors[indexColor],
                        );
                    });
                    this.series.expenseGraph.datasets[0].backgroundColor = [];
                    this.series.expenseGraph.datasets[0].data.forEach((element, index) => {
                        let indexColor = index < colors.length ? index : colors.length - 1;
                        this.series.expenseGraph.datasets[0].backgroundColor.push(
                            colors[indexColor],
                        );
                    });

                    this.load.reconcilied = true;
                },
                'json',
            );
        },
        fetchNoReconcilied: function (doc) {
            this.load[doc + 'NoReconcilied'] = false;

            const options = {
                report_reconciliation_period_start: this.filters.period.start,
                report_reconciliation_period_end: this.filters.period.end,
            };
            $.post(
                '/?_f=bankreconciliation_reconciliationStat&action=noReconcilied' + doc,
                options,
                (data) => {
                    this.series[doc] = {
                        other: {
                            listingUrl: data.doc.other.listingUrl,
                            taxesFree: NumberFormat.formatToDisplayWithCurrency(
                                data.doc.other.taxesFree,
                                numFormat,
                            ),
                            taxes: NumberFormat.formatToDisplayWithCurrency(
                                data.doc.other.taxes,
                                numFormat,
                            ),
                            amount: NumberFormat.formatToDisplayWithCurrency(
                                data.doc.other.amount,
                                numFormat,
                            ),
                        },
                        paid: {
                            listingUrl: data.doc.paid.listingUrl,
                            taxesFree: NumberFormat.formatToDisplayWithCurrency(
                                data.doc.paid.taxesFree,
                                numFormat,
                            ),
                            taxes: NumberFormat.formatToDisplayWithCurrency(
                                data.doc.paid.taxes,
                                numFormat,
                            ),
                            amount: NumberFormat.formatToDisplayWithCurrency(
                                data.doc.paid.amount,
                                numFormat,
                            ),
                        },
                    };
                    this.load[doc + 'NoReconcilied'] = true;
                },
                'json',
            );
        },
        fetchBalance: function () {
            this.load.balance = false;

            const options = {
                report_reconciliation_period_start: this.filters.period.start,
                report_reconciliation_period_end: this.filters.period.end,
            };
            $.post(
                '/?_f=bankreconciliation_reconciliationStat&action=balance',
                options,
                (data) => {
                    this.series.balanceGraph = {
                        labels: data.balanceGraph.labels,
                        datasets: [],
                    };

                    data.balanceGraph.datasets.forEach((element, index) => {
                        this.series.balanceGraph.datasets.push({
                            label: element.label,
                            borderColor: colors[index],
                            backgroundColor: 'rgba(0, 0, 0, 0.0)',
                            data: element.data,
                        });
                    });

                    this.load.balance = true;
                },
                'json',
            );
        },
        noReconciliedCard: function (step) {
            return [
                {
                    label: this.$l10n.__('Total HT'),
                    amount: step.taxesFree,
                },
                {
                    label: this.$l10n.__('Total TVA'),
                    amount: step.taxes,
                },
                {
                    label: this.$l10n.__('Total TTC'),
                    amount: step.amount,
                },
            ];
        },
        operationUrl() {
            return goToUrl('/?_f=bankreconciliation_transactions');
        },
    },
};
</script>

<style scoped lang="scss">
.doughnut {
    width: 40%;
    margin: auto;
    margin-bottom: 30px;
}

.filters {
    // Reset CSS to element.io
    .el-input__prefix,
    .el-input__suffix {
        top: -5px;
    }
    .el-date-editor .el-input__inner,
    .el-date-editor.el-input__inner {
        width: 350px;
        padding: 0 30px;
    }
}
</style>
